/**
 * ClassicCover — the default cover layout with the company brand
 * mark, site address, project title, and a verdict pill.
 *
 * SLK-H Prompt 5 extracted this from the inline classic block inside
 * SiteRagReportView.tsx. The inline editor-coupled classic JSX is
 * preserved verbatim there for back-compat with the live edit-mode
 * affordances (isEditing / setEditedReport hooks) that are local to
 * the viewer. This standalone module is the read-only Sleek-tokened
 * variant used by SLK-D (DOCX) + SLK-S (Template Studio preview)
 * downstream — neither path needs editor wiring.
 *
 * Invariants preserved (PX pack `00465-fdt`):
 *   - getDisplayVerdict(report) drives the verdict pill text + tone
 *   - The rotated cyan "STRICTLY CONFIDENTIAL" rail is owned by
 *     `<div data-page="1">` in SiteRagReportView — cover layouts
 *     render INSIDE the rail, not around it. Untouched here.
 *   - data-testid="cover-classic" stamped (consistent with
 *     cover-minimal / cover-data-dense / cover-hero-image testids).
 */
import React from 'react';
import { PreparedForLine } from '../../PreparedForLine';
import { resolveCoverChipsForVariant } from '../../../../lib/reportRendering/coverKpiChips';
import { CoverChipField } from './CoverChipField';
import type { CoverProps } from './types';
import { CoverCustomerLogo, customerLogoImgClass } from './CoverCustomerLogo';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { resolveAbstractExcerpt, resolveAbstractFull, resolveCoverTitleLines, resolveSiteWordmark } from '../../../../lib/reportRendering/journalCoverData';
import { ReportPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

export const ClassicCover: React.FC<CoverProps> = ({
  report, theme, branding, isAdmin,
  pickerOpen, onTogglePicker, onSelectArchetype,
  disclaimer,
  isEditing, editedAddress, onAddressChange,
  boundedStandfirst = false,
}) => {
  // FIX-B — the cover wordmark goes through the shared `resolveSiteWordmark`
  // resolver (branding → report.siteName → printable id → report title), which
  // treats the "Unnamed site" sentinel a supplied `branding` may carry as a
  // FALLTHROUGH and never ships that placeholder on a real cover. Previously
  // this read `effectiveBranding.siteWordmark` raw, so a harness/export branding
  // object whose `siteWordmark` was the sentinel surfaced "UNNAMED SITE" even
  // when `report.siteName` was populated.
  const siteWordmark = resolveSiteWordmark(report, branding);
  // L-id-leak — never fall back to the raw Firestore `report.id` (it leaks an
  // internal doc id onto the cover). Use the human placeholder instead.
  const fallbackSiteWordmark = report.siteName?.trim() || 'Unnamed site';
  const effectiveBranding = branding ?? {
    audienceMode: 'internal',
    siteWordmark: fallbackSiteWordmark,
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  // P3 — the cover title comes from the ONE resolver (config-label source of
  // truth); only the per-line <br/> presentation stays local. Replaces the
  // 4th duplicated inline per-type title switch and gains the
  // financial / public-sentiment / Go-No-Go correctness automatically.
  const titleLines = resolveCoverTitleLines(report);
  const titleLabel = titleLines.map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {line}
    </React.Fragment>
  ));

  // TCP/report-cover consistency: derive the cover title size from the
  // theme's headingScale instead of a fixed `text-5xl`. The editorial
  // h2 baseline is 1.5rem; the classic display title historically read
  // at text-5xl (3rem) ≈ 2× the h2, so we keep that 2× display ratio
  // while letting technical/compact presets scale the title down in
  // step with section headings. (No fixed px — tracks the theme.)
  const titleFontSize = `calc(${reportTypographySizes(theme).h2} * 2)`;

  const abstract = boundedStandfirst
    ? resolveAbstractExcerpt(report)
    : resolveAbstractFull(report);

  return (
    <div className="relative space-y-8 pb-14" data-testid="cover-classic" data-cover-variant="classic">
      <div className="flex justify-between items-start">
        <div className="space-y-4">
          <div
            className="text-xl font-bold tracking-tighter"
            style={{ color: reportHeadingInk(theme) }}
            data-testid="cover-site-name-header"
          >
            {siteWordmark}
          </div>
          <div className="space-y-1">
            {isEditing && onAddressChange ? (
              <input
                className="text-sm font-medium w-full bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                style={{ color: roleColor(theme, 'muted') }}
                value={editedAddress ?? report.address}
                onChange={(e) => onAddressChange(e.target.value)}
              />
            ) : (
              <div className="text-sm font-medium" style={{ color: roleColor(theme, 'muted') }}>{report.address}</div>
            )}
            <h1
              id="cover-title"
              className="font-bold leading-tight tracking-tight max-w-2xl"
              style={{
                fontFamily: theme.displayFont || theme.headingFont,
                color: reportHeadingInk(theme),
                fontSize: titleFontSize,
              }}
            >
              {titleLabel}
            </h1>
          </div>
        </div>
        <div className="text-right space-y-2">
          {effectiveBranding.primaryLogoUrl && (
            <img
              src={effectiveBranding.primaryLogoUrl}
              alt={effectiveBranding.primaryLogoAlt}
              className="h-6 max-w-[104px] object-contain ml-auto"
              data-testid="cover-classic-logo-primary"
            />
          )}
          {effectiveBranding.audienceMode === 'customer' && effectiveBranding.secondaryLogoUrl && (
            <CoverCustomerLogo
              src={effectiveBranding.secondaryLogoUrl}
              alt={effectiveBranding.secondaryLogoAlt}
              tone="on-light"
              className={customerLogoImgClass('on-light', 'h-6 max-w-[104px] ml-auto')}
              data-testid="cover-classic-logo-secondary"
            />
          )}
        </div>
      </div>

      {/* TCP-4 (row 0.99233) — KPI chip strip. Power + Cooling +
           Acres from the canonical chip resolver (coverKpiChips.ts).
           Pre-TCP-4 Classic carried no chip surface so switching from
           data-dense to classic in Studio dropped the headline intent
           numbers silently. We render `—` for undecided chips and
           collapse the whole strip when ALL three values are null
           (a freshly-created template with no intent yet). */}
      {(() => {
        const chips = resolveCoverChipsForVariant(report, 'classic');
        const allEmpty = chips.every(c => c.value === null);
        if (allEmpty) return null;
        return (
          <div
            className="flex items-baseline gap-6 flex-wrap"
            data-testid="cover-classic-kpis"
          >
            {chips.map(chip => (
              <div key={chip.id} data-testid={`cover-classic-chip-${chip.id}`}>
                <CoverChipField chip={chip} report={report} theme={theme} testIdSuffix={`classic-${chip.id}`} />
              </div>
            ))}
          </div>
        );
      })()}

      <PreparedForLine
        report={report}
        isAdmin={isAdmin}
        pickerOpen={pickerOpen}
        onTogglePicker={onTogglePicker}
        onSelectArchetype={onSelectArchetype}
      />

      {/* ABSTRACT (decision 5) — the cover-carried executive summary, IN
           FULL. Collapses cleanly when the report has no summary. */}
      {abstract && (
        <div
          className="pt-6 border-t max-w-2xl"
          style={{ borderTopColor: roleColor(theme, 'hair') }}
          data-testid="cover-classic-abstract"
        >
          <div
            className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
            style={{ color: roleColor(theme, 'accent') }}
          >
            Abstract
          </div>
          <p
            className="text-sm leading-relaxed"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont }}
          >
            {abstract}
          </p>
        </div>
      )}

      {/* GATEWAY POWER platform credit — left-aligned just BELOW the cover box
          (absolute, top: 100% + 3px): it lands in the cover shell's bottom
          padding on screen and in the empty rest of the page-tall #cover in
          print, so it adds no height and never meets the bottom-right
          disclaimer, whose width-dependent wrap collided with a bottom-left
          credit on phones. Rendered BEFORE the disclaimer with its margin
          zeroed: Tailwind v4 `space-y-*` margins every child but the last, and
          the disclaimer must stay the last child to keep its exact position. */}
      <ReportPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        className="absolute left-0 top-[calc(100%_+_3px)]"
        style={{ marginBlock: 0 }}
        testId="cover-classic-platform-credit"
      />

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote (COVER-CONTENT BATCH 4),
          rendered at 80% of the prior 10px size (→ 8px). */}
      <div
        className="absolute bottom-0 right-0 text-right text-[8px] leading-relaxed max-w-md"
        style={{ color: roleColor(theme, 'muted') }}
        data-testid="cover-classic-disclaimer"
      >
        {disclaimer}
      </div>
    </div>
  );
};
